import { useEffect } from 'react';
import { Link } from 'react-router';

import { ConteudoDoDocumento, LinksLegais } from '@/comuns/documentoLegal';
import { DOCUMENTOS, EMPRESA, PRODUTO } from '@/data/juridico';
import type { IdDoDocumento } from '@/data/juridico';
import { aplicarTema, observarSistema, temaSalvo } from '@/tema';
import logoSobreClaro from '@assets/formulamed-logo.png';
import logoSobreEscuro from '@assets/formulamed-logo-branco.png';

/**
 * Termos de Uso, Política de Privacidade e LGPD, cada um na própria página pública.
 *
 * Fora das surfaces de propósito: abre com ou sem sessão, porque o Google (verificação do app)
 * e o aceite do cadastro apontam para cá. O texto vem de `src/data/juridico.ts`.
 */
export function DocumentoLegalTela({ id }: { id: IdDoDocumento }) {
    const doc = DOCUMENTOS[id];

    useEffect(() => {
        const tema = temaSalvo();
        aplicarTema(tema);
        return observarSistema(tema, () => aplicarTema(tema));
    }, []);

    useEffect(() => {
        const antes = document.title;
        document.title = `${doc.titulo} · ${PRODUTO.nome}`;
        return () => { document.title = antes; };
    }, [doc]);

    return (
        <div style={{ minHeight: '100vh', background: 'var(--surface-secondary)', fontFamily: 'var(--font-ui)' }}>
            <header style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ maxWidth: 860, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    {/* O logotipo é arte proprietária: nunca redigitado em fonte do sistema. */}
                    <Link to="/" aria-label={`${PRODUTO.nome} — início`} style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <img src={logoSobreClaro} alt="" data-vec-logo="light" style={{ height: 24, width: 'auto' }} />
                        <img src={logoSobreEscuro} alt="" data-vec-logo="dark" style={{ height: 24, width: 'auto' }} />
                    </Link>
                    <LinksLegais atual={id} />
                </div>
            </header>

            <main style={{ maxWidth: 860, margin: '0 auto', padding: '28px 16px 56px' }}>
                <article style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'clamp(20px, 5vw, 44px)' }}>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                        Versão {doc.versao} · vigente desde {doc.vigenteDesde}
                    </p>
                    <h1 style={{ margin: '8px 0 0', fontFamily: 'var(--font-display)', fontSize: 30, lineHeight: 1.2, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {doc.titulo}
                    </h1>
                    <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: '24px', color: 'var(--text-secondary)', maxWidth: '62ch' }}>{doc.resumo}</p>

                    <nav aria-label="Sumário" style={{ margin: '24px 0 32px', padding: '14px 18px', background: 'var(--surface-secondary)', borderRadius: 'var(--radius-md)' }}>
                        <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Sumário</p>
                        <ol style={{ margin: 0, paddingLeft: 20, columns: '2 15rem', columnGap: 28, fontSize: 13.5, lineHeight: '24px' }}>
                            {doc.secoes.map((s) => (
                                <li key={s.id}><a href={`#${s.id}`} style={{ color: 'var(--text-link)' }}>{s.titulo}</a></li>
                            ))}
                        </ol>
                    </nav>

                    <ConteudoDoDocumento id={id} />
                </article>

                <footer style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', textAlign: 'center', fontSize: 12.5, lineHeight: '19px', color: 'var(--text-tertiary)' }}>
                    <p style={{ margin: 0 }}>{EMPRESA.razaoSocial} · CNPJ {EMPRESA.cnpj}<br />{EMPRESA.endereco}</p>
                    <LinksLegais atual={id} />
                </footer>
            </main>
        </div>
    );
}
